import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Pagination, Typography } from '@mui/material';
import { VscListSelection } from 'react-icons/vsc';
import LogsHeaderSelector from './headerSelector';
import useClickOutside from 'hooks/useClickOutside';
import { useAppSelector } from 'hooks/useRedux';
import { customerLogReport } from 'store/reducer/workflowReducer';

interface Header {
  name: string;
  key: string;
  show: boolean;
}

export default function CommunicationLogsTable() {
  const pageLimit = 10;

  const [headers, setHeaders] = React.useState<Array<string>>([]);
  const [logsData, setLogsData] = React.useState<
    Array<Record<string, unknown>>
  >([]);
  const [tableData, setTableData] = React.useState<
    Array<Record<string, unknown>>
  >([]);
  const [showDropdown, setShowDropdown] = React.useState<boolean>(false);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [currentPage, setCurrentPage] = React.useState<number>(1);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const logData: any = useAppSelector(customerLogReport);

  React.useEffect(() => {
    if (logData) {
      const theads = logData?.headers
        .filter((header: Header) => header.show)
        .map((header: Header) => header.name);

      setHeaders(theads);
      setLogsData(logData?.results);
      setTableData(logData?.results.slice(0, pageLimit));
      setPageCount(logData?.count ? Math.ceil(logData.count / pageLimit) : 0);
    }
  }, [logData]);

  const onHeaderSelect = (checked: boolean, data: Record<string, any>) => {
    let newHeaders = [...headers];
    const indexOrder = logData?.Header.map((header: Header, index: number) => ({
      name: header.name,
      order: index + 1,
    }));
    const orderMap: any = new Map(
      indexOrder.map((item: any) => [item.name, item.order])
    );

    if (checked) {
      newHeaders.push(data.name);
    } else {
      newHeaders = newHeaders.filter((header) => header !== data.name);
    }
    newHeaders.sort((a, b) => orderMap.get(a)! - orderMap.get(b)!);
    setHeaders(newHeaders);
  };

  useClickOutside(headerRef, () => showDropdown && setShowDropdown(false));

  const handlePagination = async (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    const offset = pageLimit * (page - 1);
    const data = logsData.slice(offset, offset + pageLimit);
    setTableData(data);
    setCurrentPage(page);
  };

  return (
    <Box mt='1.5rem'>
      <Pagination
        count={pageCount}
        variant='outlined'
        shape='rounded'
        page={currentPage}
        onChange={handlePagination}
        sx={{
          '.Mui-selected': {
            background: '#004357 !important',
            color: '#fff',
          },
          marginBottom: '1rem',
        }}
      />

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              {headers?.map((header) => (
                <TableCell key={header} align='center'>
                  {header}
                </TableCell>
              ))}
              {logsData?.length ? (
                <TableCell>
                  <Box position='relative'>
                    <button onClick={() => setShowDropdown((prev) => !prev)}>
                      <VscListSelection />
                    </button>

                    {showDropdown && (
                      <LogsHeaderSelector
                        ref={headerRef}
                        headers={headers}
                        data={logData.Header}
                        onHeaderSelect={onHeaderSelect}
                      />
                    )}
                  </Box>
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {logsData?.length
              ? tableData?.map((log: any, index: number) => (
                  <TableRow
                    key={`log-${log.log_id}-${index}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    {headers.map((header) => {
                      const headerObj = logData?.headers.find(
                        (el: Header) => el.name === header
                      );
                      return (
                        <TableCell key={header} align='center'>
                          {log[headerObj!.key]}
                        </TableCell>
                      );
                    })}
                    <TableCell />
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
        {!logsData.length ? (
          <Box
            sx={{ minHeight: '200px', display: 'grid', placeContent: 'center' }}
          >
            <Typography fontSize='14px'>
              No logs found for applied filter.
            </Typography>
          </Box>
        ) : null}
      </TableContainer>
    </Box>
  );
}
